import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  Sparkles, Check, ArrowLeft, Loader2, Crown, Zap, Clock,
  Download, BookmarkCheck, Shield, CreditCard, Star
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import ThemeToggle from '@/components/ThemeToggle';
import Logo from '@/components/Logo';
import AppNav from '@/components/AppNav';
import SEO from '@/components/SEO';
import CurrencyToggle, { PriceShownNote } from '@/components/CurrencyToggle';
import { useRegion } from '@/context/RegionContext';
import { PLAN_PRICES } from '@/lib/plans';
import { trackEvent } from '@/lib/metaPixel';
import { onConsentChange } from '@/lib/consent';

const API = process.env.REACT_APP_BACKEND_URL;

const PricingPage = ({ lockedRegion = null }) => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(null); // Track which plan is loading
  const [affiliateCode, setAffiliateCode] = useState('');
  const { user, isPro, isBasic, getTrialDaysLeft, isAuthenticated, getAuthHeader, refreshUser } = useAuth();
  const { formatPrice, setRegion } = useRegion();
  const navigate = useNavigate();
  const trialDaysLeft = getTrialDaysLeft();

  // Fire a Meta Pixel ViewContent when the pricing page is viewed. Also re-fire
  // if the visitor accepts consent while already on this page (the pixel didn't
  // exist at mount, so the first call would have no-opped).
  useEffect(() => {
    trackEvent('ViewContent', { content_name: 'Pricing' });
    return onConsentChange((v) => {
      if (v === 'accepted') trackEvent('ViewContent', { content_name: 'Pricing' });
    });
  }, []);

  // Country-locked variant (/us/pricing, /uk/pricing) — force the region
  // context so the rest of the funnel honours it, and hide the currency toggle.
  useEffect(() => {
    if (lockedRegion === 'us' || lockedRegion === 'uk') {
      setRegion(lockedRegion, { persistToServer: false });
    }
  }, [lockedRegion, setRegion]);

  // Register CTA target — carry the locked region through to /register so a
  // brand-new visitor lands in their correct region from day one.
  const registerHref = (plan) => {
    const params = new URLSearchParams();
    if (plan) params.set('plan', plan);
    if (lockedRegion) params.set('region', lockedRegion);
    const qs = params.toString();
    return `/register${qs ? `?${qs}` : ''}`;
  };

  // Check for referral code in URL or localStorage
  useEffect(() => {
    const refCode = searchParams.get('ref') || localStorage.getItem('kylo_ref_code');
    if (refCode) {
      setAffiliateCode(refCode.toUpperCase());
    }
  }, [searchParams]);

  const handleUpgrade = async (planType) => {
    trackEvent('InitiateCheckout', {
      content_name: planType,
      value: PLAN_PRICES[planType]?.gbp,
      currency: 'GBP',
    });
    if (!isAuthenticated) {
      // Carry the chosen plan + region through registration so we can skip the
      // trial and send them straight to Stripe checkout once their account exists.
      navigate(registerHref(planType));
      return;
    }

    setIsLoading(planType);
    try {
      // Get the current origin URL for Stripe redirect
      const originUrl = window.location.origin;
      
      // Create checkout session with the plan type and affiliate code
      const response = await axios.post(`${API}/api/payments/create-checkout`, 
        { 
          origin_url: originUrl, 
          plan_type: planType,
          affiliate_code: affiliateCode || null
        },
        { headers: getAuthHeader() }
      );
      
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to start checkout. Please try again.';
      toast.error(errorMsg);
      setIsLoading(null);
    }
  };

  const freeFeatures = [
    '3-day free trial',
    'Viral script writer: hooks, angles & scripts',
    'Viral video transcriber',
    'One-click AI voiceovers',
    'Trending product research (last 7 days)',
    '2 violation appeals during trial',
    'Save up to 10 ideas'
  ];

  const basicFeatures = [
    'Viral script writer: 3 hooks, angles & scripts',
    'Viral video transcriber',
    'One-click AI voiceovers',
    'Trending product research (last 7 days)',
    'Creator dashboard & income calculator',
    '5 violation appeals per month',
    '80 content generations per month',
    'Unlimited saved ideas',
    'Cancel anytime'
  ];

  const proFeatures = [
    'Everything in Basic, plus:',
    '5 text hooks per product (vs 3)',
    'Trending product research (last 30 days)',
    '20 violation appeals per month',
    '200 content generations per month',
    'Re-spin hooks for fresh ideas',
    'Export all content',
    'Priority AI processing',
    'Cancel anytime'
  ];

  // Determine current plan
  const getCurrentPlan = () => {
    if (isPro) return 'pro';
    if (isBasic) return 'basic';
    return 'free';
  };

  const currentPlan = getCurrentPlan();

  return (
    <div className="min-h-screen bg-background">
      <SEO title="Plans &amp; Pricing" description="Simple monthly pricing for TikTok Shop and UGC creators. Basic £9.99/mo, Pro £15.99/mo. Cancel anytime — no lock-in." />
      {/* Animated background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-20 -left-40 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-secondary/10 rounded-full blur-3xl" />
      </div>

      {/* Navigation */}
      <nav className="relative z-50 border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <AppNav />
          </div>
        </div>
      </nav>

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-6">
            <Crown className="w-4 h-4 text-accent" />
            <span className="text-sm text-muted-foreground">Simple, transparent pricing</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mb-4">
            Unlock <span className="gradient-text-accent">Unlimited</span> Viral Content
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Start with a free trial, then choose the plan that works best for you.
          </p>
        </div>

        {/* Trial Status Banner */}
        {isAuthenticated && currentPlan === 'free' && trialDaysLeft !== null && (
          <div className={`mb-8 p-4 rounded-2xl ${trialDaysLeft <= 2 ? 'bg-destructive/10 border border-destructive/30' : 'glass-card'}`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <Clock className={`w-5 h-5 ${trialDaysLeft <= 2 ? 'text-destructive' : 'text-primary'}`} />
                <div>
                  <p className="font-medium">
                    {trialDaysLeft > 0 
                      ? `${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''} left in your free trial`
                      : 'Your free trial has expired'
                    }
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {trialDaysLeft > 0 
                      ? 'Upgrade now to keep creating viral content'
                      : 'Upgrade to continue using Poettik'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="flex flex-col items-center mb-6 gap-2">
          {!lockedRegion && <CurrencyToggle />}
          <PriceShownNote className="text-center" />
        </div>
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {/* Free Trial */}
          <Card variant="glass" className="relative">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                  <Zap className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <CardTitle>Free Trial</CardTitle>
                  <CardDescription>Try before you buy</CardDescription>
                </div>
              </div>
              <div className="mt-4">
                <span className="font-display text-4xl font-bold">{formatPrice(PLAN_PRICES.trial)}</span>
                <span className="text-muted-foreground">/3 days</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {freeFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <span className="text-sm text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
              {!isAuthenticated ? (
                <Link to={registerHref(null)} className="w-full">
                  <Button variant="outline" size="lg" className="w-full">
                    Start Free Trial
                  </Button>
                </Link>
              ) : (
                <Button variant="outline" size="lg" className="w-full" disabled>
                  {currentPlan === 'free' ? 'Current Plan' : 'Trial Used'}
                </Button>
              )}
            </CardFooter>
          </Card>

          {/* Basic Plan */}
          <Card variant="glass" className="relative">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                  <Star className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle>Basic</CardTitle>
                  <CardDescription>For content creators</CardDescription>
                </div>
              </div>
              <div className="mt-4">
                <span className="font-display text-4xl font-bold">{formatPrice(PLAN_PRICES.basic)}</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Billed monthly, cancel anytime</p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {basicFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <span className="text-sm text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
              <Button 
                variant="gradient" 
                size="lg" 
                className="w-full"
                onClick={() => handleUpgrade('basic')}
                disabled={isLoading === 'basic' || currentPlan === 'basic' || currentPlan === 'pro'}
              >
                {isLoading === 'basic' ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : currentPlan === 'basic' ? (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Current Plan
                  </>
                ) : currentPlan === 'pro' ? (
                  'You have Pro'
                ) : (
                  <>
                    <Star className="w-5 h-5 mr-2" />
                    Get Basic
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>

          {/* Pro Plan */}
          <Card variant="gradient" className="relative">
            {/* Most Popular badge */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <div className="px-4 py-1 rounded-full bg-gradient-to-r from-accent to-secondary text-accent-foreground text-sm font-medium">
                Most Popular
              </div>
            </div>
            
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent/20 to-secondary/20 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <CardTitle>Pro</CardTitle>
                  <CardDescription>For serious creators</CardDescription>
                </div>
              </div>
              <div className="mt-4">
                <span className="font-display text-4xl font-bold">{formatPrice(PLAN_PRICES.pro)}</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Billed monthly, cancel anytime</p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {proFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                    <span className={`text-sm ${index === 0 ? 'text-accent font-medium' : 'text-foreground'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
              <Button 
                variant="premium" 
                size="lg" 
                className="w-full"
                onClick={() => handleUpgrade('pro')}
                disabled={isLoading === 'pro' || currentPlan === 'pro'}
              >
                {isLoading === 'pro' ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : currentPlan === 'pro' ? (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    You&apos;re on Pro!
                  </>
                ) : (
                  <>
                    <Crown className="w-5 h-5 mr-2" />
                    Upgrade to Pro
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>

        {/* Features Grid */}
        <div className="text-center mb-12">
          <h2 className="font-display text-2xl font-bold mb-4">Why Upgrade?</h2>
        </div>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {[
            { icon: <Zap className="w-5 h-5" />, title: 'More Monthly Generations', desc: 'Pro gives you 200 generations every month to power your content' },
            { icon: <BookmarkCheck className="w-5 h-5" />, title: 'Unlimited Saves', desc: 'Build your content library' },
            { icon: <Download className="w-5 h-5" />, title: 'Export Anytime', desc: 'Download all your ideas' },
            { icon: <Shield className="w-5 h-5" />, title: 'Cancel Anytime', desc: 'No lock-in, cancel whenever you like' },
          ].map((item, index) => (
            <Card key={index} variant="glass" className="p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mx-auto mb-4 text-primary">
                {item.icon}
              </div>
              <h3 className="font-semibold mb-2">{item.title}</h3>
              <p className="text-sm text-muted-foreground">{item.desc}</p>
            </Card>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="text-center mb-8">
          <h2 className="font-display text-2xl font-bold mb-4">Questions?</h2>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { q: 'What happens after my trial ends?', a: 'You\'ll need to upgrade to Basic or Pro to continue generating content. Your saved ideas will remain accessible.' },
            {
              q: 'Is this a monthly subscription?',
              a: lockedRegion === 'us'
                ? 'Yes! Basic is $11.99/month and Pro is $19.99/month. Billing is processed in GBP via Stripe and converted to USD at checkout by your card issuer. You\'re billed monthly and can cancel anytime.'
                : lockedRegion === 'uk'
                  ? 'Yes! Basic is £9.99/month and Pro is £15.99/month. Billing is in GBP via Stripe. You\'re billed monthly and can cancel anytime.'
                  : 'Yes! Basic is £9.99/month ($11.99 USD) and Pro is £15.99/month ($19.99 USD). Billing is in GBP — Stripe converts to your local card currency at checkout. You\'re billed monthly and can cancel anytime.',
            },
            { q: 'Can I cancel anytime?', a: 'Absolutely! You can cancel your subscription at any time from your account settings. You\'ll keep access until the end of your current billing period.' },
            { q: 'What payment methods do you accept?', a: 'We accept all major credit cards and debit cards. Payments are processed securely through Stripe.' },
            { q: 'What\'s the difference between Basic and Pro?', a: 'Pro gives you 5 text hooks instead of 3, 200 content generations per month instead of 80, 20 violation appeals per month instead of 5, trending product data going back 30 days instead of 7, the ability to re-spin hooks for fresh ideas, and priority AI processing.' },
            { q: 'Can I upgrade from Basic to Pro later?', a: 'Yes! You can upgrade from Basic to Pro at any time. Simply cancel your Basic subscription and subscribe to Pro.' },
          ].map((item, index) => (
            <Card key={index} variant="glass" className="p-6">
              <h3 className="font-semibold mb-2">{item.q}</h3>
              <p className="text-sm text-muted-foreground">{item.a}</p>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
};

export default PricingPage;
